import { LoadingOverlay } from "@mantine/core";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import type { AppDispatch, RootState } from "./store/store";
import { navigationService } from "./services/navigationService";
import { initializeAuth } from "./store/auth/authSlice";

// Components
import Login from "./components/auth/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Dashboard from "./components/dashboard/Dashboard";
import Layout from "./components/layout/Layout";

// Pages
import AccountsPage from "./pages/AccountsPage";
import ActivityPage from "./pages/ActivityPage";
import ApiKeysPage from "./pages/ApiKeysPage";
import LicensesPage from "./pages/LicensesPage";
import LicenseTypesPage from "./pages/LicenseTypesPage";
import UsersPage from "./pages/UsersPage";

function App() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated, isInitializing } = useSelector(
		(state: RootState) => state.auth
	);

	// Initialize navigation service
	useEffect(() => {
		navigationService.setNavigate(navigate);
	}, [navigate]);

	// Initialize auth on first mount if we have a stored token
	useEffect(() => {
		const token = localStorage.getItem("token");
		if (token && !isAuthenticated && !isInitializing) {
			dispatch(initializeAuth());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Navigation restoration after authentication
	useEffect(() => {
		// Restore route when authenticated and on root, login, or dashboard (initial landing)
		if (
			isAuthenticated &&
			(location.pathname === "/" ||
				location.pathname === "/login" ||
				location.pathname === "/dashboard")
		) {
			const redirectPath = localStorage.getItem("redirectPath");
			const lastRoute = navigationService.getLastRoute();

			// Only restore if there's a specific route to restore (not dashboard)
			if (redirectPath && redirectPath !== "/dashboard") {
				localStorage.removeItem("redirectPath"); // Clear after use
				navigate(redirectPath, { replace: true });
			} else if (
				lastRoute &&
				lastRoute !== "/" &&
				lastRoute !== "/login" &&
				lastRoute !== "/dashboard" &&
				location.pathname !== lastRoute
			) {
				navigate(lastRoute, { replace: true });
			}
			// If already on dashboard or no specific route to restore, stay on dashboard
		}
	}, [isAuthenticated, location.pathname, navigate]);

	// Save current route when navigating (for page reloads and session restoration)
	useEffect(() => {
		// Save valid routes regardless of auth state (before potential redirect to login)
		if (location.pathname !== "/" && location.pathname !== "/login") {
			console.log("[App] Saving route:", location.pathname);
			navigationService.saveLastRoute(location.pathname);
		}
	}, [location.pathname]);

	if (isInitializing) return <LoadingOverlay visible />;

	if (!isAuthenticated) {
		// Save the current route BEFORE redirecting to login (for restoration after login)
		const currentPath = window.location.pathname;
		if (currentPath !== "/" && currentPath !== "/login") {
			console.log("[App] Saving route before redirect to login:", currentPath);
			navigationService.saveLastRoute(currentPath);
		}

		return (
			<Routes>
				<Route
					path="/login"
					element={<Login />}
				/>
				<Route
					path="*"
					element={
						<Navigate
							to="/login"
							replace
						/>
					}
				/>
			</Routes>
		);
	}

	return (
		<Layout>
			<Routes>
				{/* /login is handled by the navigation restoration effect above */}

				<Route
					path="/dashboard"
					element={
						<ProtectedRoute>
							<Dashboard />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/accounts"
					element={
						<ProtectedRoute>
							<AccountsPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/licenses"
					element={
						<ProtectedRoute>
							<LicensesPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/license-types"
					element={
						<ProtectedRoute requiredRoles={["admin", "manager"]}>
							<LicenseTypesPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/users"
					element={
						<ProtectedRoute requiredRoles={["admin", "manager"]}>
							<UsersPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/activity"
					element={
						<ProtectedRoute requiredRoles={["admin", "manager"]}>
							<ActivityPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/api-keys"
					element={
						<ProtectedRoute requiredRoles={["admin"]}>
							<ApiKeysPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="*"
					element={
						<Navigate
							to="/dashboard"
							replace
						/>
					}
				/>
			</Routes>
		</Layout>
	);
}

export default App;
